import React from 'react';
import { Card } from '../ui/Card';
import { User, MetricData, ConversionRatio } from '../../types/dashboard';
import { Badge } from '../ui/Badge';
interface ComparisonTableProps {
  users: User[];
  metrics: Record<string, MetricData>;
  ratios: Record<string, ConversionRatio[]>;
}
export function ComparisonTable({
  users,
  metrics,
  ratios
}: ComparisonTableProps) {
  const metricKeys: (keyof MetricData)[] = ['calls', 'transfers', 'applicationsTaken', 'documentsOut', 'submissions'];
  const metricLabels = {
    calls: 'Total Calls',
    transfers: 'Transfers',
    applicationsTaken: 'Applications',
    documentsOut: 'Docs Out',
    submissions: 'Submissions'
  };
  // Helper to find max and min values for color coding
  const getMaxValue = (key: keyof MetricData) => {
    return Math.max(...users.map(u => metrics[u.id]?.[key] || 0));
  };
  const getMinValue = (key: keyof MetricData) => {
    return Math.min(...users.map(u => metrics[u.id]?.[key] || 0));
  };
  // Helper to determine cell background color based on performance
  const getCellColor = (value: number, key: keyof MetricData) => {
    const max = getMaxValue(key);
    const min = getMinValue(key);
    if (value === max && value > 0) return 'bg-green-50'; // Top performer
    if (value === min && users.length > 2) return 'bg-red-50'; // Bottom performer
    return 'bg-white'; // Middle
  };
  return <Card title="Team Comparison" noPadding className="overflow-hidden" data-id="element-12">
      <div className="overflow-x-auto" data-id="element-13">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-14">
          <thead className="bg-gray-50" data-id="element-15">
            <tr data-id="element-16">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 border-r border-gray-200" data-id="element-17">
                Metric
              </th>
              {users.map(user => <th key={user.id} scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-18">
                  <div className="flex flex-col items-center" data-id="element-19">
                    <span className="font-bold text-gray-900" data-id="element-20">{user.name}</span>
                    <span className="text-[10px] text-gray-400 font-normal" data-id="element-21">
                      {user.role}
                    </span>
                  </div>
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-22">
            {/* Raw Metrics Rows */}
            <tr className="bg-gray-50/50" data-id="element-23">
              <td colSpan={users.length + 1} className="px-6 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-24">
                Volume Metrics
              </td>
            </tr>
            {metricKeys.map(key => {
            const maxVal = getMaxValue(key);
            const minVal = getMinValue(key);
            return <tr key={key} className="hover:bg-gray-50" data-id="element-25">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white border-r border-gray-200" data-id="element-26">
                    {metricLabels[key]}
                  </td>
                  {users.map(user => {
                const val = metrics[user.id]?.[key] || 0;
                const isTop = val === maxVal && val > 0;
                const isBottom = val === minVal && users.length > 2 && val > 0;
                const cellColor = getCellColor(val, key);
                return <td key={user.id} className={`px-6 py-4 whitespace-nowrap text-sm text-center ${cellColor} transition-colors`} data-id="element-27">
                        <div className="flex items-center justify-center gap-2" data-id="element-28">
                          <span className="font-semibold" data-id="element-29">
                            {val.toLocaleString()}
                          </span>
                          {isTop && <Badge variant="success" size="sm" data-id="element-30">
                              Top
                            </Badge>}
                          {isBottom && <Badge variant="danger" size="sm" data-id="element-31">
                              Low
                            </Badge>}
                        </div>
                      </td>;
              })}
                </tr>;
          })}

            {/* Conversion Ratios Rows */}
            <tr className="bg-gray-50/50" data-id="element-32">
              <td colSpan={users.length + 1} className="px-6 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-33">
                Conversion Ratios
              </td>
            </tr>
            {ratios[users[0]?.id]?.map((ratioTemplate, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-34">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white border-r border-gray-200" data-id="element-35">
                  {ratioTemplate.label}
                </td>
                {users.map(user => {
              const userRatios = ratios[user.id] || [];
              const ratio = userRatios[idx];
              const isLow = ratio ? ratio.value < ratio.threshold : false;
              return <td key={user.id} className={`px-6 py-4 whitespace-nowrap text-sm text-center ${isLow ? 'bg-red-50' : 'bg-white'} transition-colors`} data-id="element-36">
                      <span className={`font-medium ${isLow ? 'text-red-600' : 'text-gray-900'}`} data-id="element-37">
                        {ratio?.value.toFixed(1)}%
                      </span>
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
    </Card>;
}